import { Component, Injector, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { AbstractComponent, Attendance, AttendanceStatusEnum, Contact, ContactService, CustomField, CustomFieldContact, CustomFieldService } from 'lib-trend-core';
import { Observable } from 'rxjs';
import { getAttendance } from '../../state/actions';
import { AppState } from '../../state/app.state';
import { attendanceSelector } from '../../state/selectors';

@Component({
    selector: 'custom-field-attendance',
    // templateUrl: './custom-field-attendance.component.html',
    templateUrl: './custom-field-attendance.v2.component.html',
    styleUrls: ['./custom-field-attendance.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomFieldAttendanceComponent extends AbstractComponent implements OnInit {
  contact!: Contact;

  readonly attendance$: Observable<Attendance> = this.store.select(attendanceSelector);

  listCustomField: Array<CustomFieldContact> = new Array<CustomFieldContact>();
  listFieldsCustom: Array<CustomField> = new Array<CustomField>();

  attendance: Attendance;

  showSelectCustomField: boolean = true;

  constructor(
    injector: Injector,
    private store: Store<AppState>,
    public customFieldService: CustomFieldService,
    public contactService: ContactService,
  ) {
    super(injector);
    this.createForm();
  }

  ngOnInit() {
    this.attendance$.subscribe((attendance: Attendance) => {
      this.attendance = attendance;
      this.contact = attendance?.contact;

      this.formGroup = this.formBuilder.group({
        selectedFields: [[]]
      });

      if (this.contact?.customFields) {
        const selectedFieldIds = this.contact?.customFields.map(field => field._id) || [];
        this.formGroup.get('selectedFields')?.setValue(selectedFieldIds);

        this.contact?.customFields.forEach(field => {
          this.formGroup.addControl(field._id, this.formBuilder.control(field.value));
        });

        this.contact.customFields.forEach(field => {
          this.formGroup.addControl(field._id, this.formBuilder.control(field.value || ''));
        });
        this.listCustomField = this.contact.customFields;
      }
    });
    this.getListCustomField();
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      selectedFields: [[]]
    });
  }

  getListCustomField(): void {
    this.customFieldService.getList().subscribe((list: Array<CustomField>) => {
      this.listFieldsCustom = list;
    });
  }

  toogleSelectCustomField(): void {
    this.showSelectCustomField = false;
  }

  addCustomField(selectedIds: Array<string>): void {
    const updatedContact = {
      ...this.contact,
      customFields: [...(this.contact?.customFields || [])]
    };

    const currentIds = updatedContact.customFields.map(field => field._id);

    const addedFields = selectedIds.filter(id => !currentIds.includes(id));

    const removedFields = currentIds.filter(id => !selectedIds.includes(id));

    addedFields.forEach(idCustomField => {
      const selectedField = this.listFieldsCustom.find(field => field._id === idCustomField);
      if (selectedField) {
        updatedContact.customFields.push({
          _id: selectedField._id,
          name: selectedField.name,
          description: selectedField.description,
          type: selectedField.type,
          value: ''
        });
        this.formGroup.addControl(selectedField._id, this.formBuilder.control(null));
      }
    });

    removedFields.forEach(idCustomField => {
      updatedContact.customFields = updatedContact.customFields.filter(field => field._id !== idCustomField);
      if (this.formGroup.contains(idCustomField)) {
        this.formGroup.removeControl(idCustomField);
      }
    });

    this.contact = updatedContact;
    this.updateContact();
  }

  isFieldDirty(idCustomField: string): boolean {
    const control = this.formGroup.get(idCustomField);
    if (!control) return false;

    const originalValue = this.contact.customFields.find(f => f._id === idCustomField)?.value;
    return control.value !== originalValue;
  }

  editCustomField(idCustomField: string): void {
    const newValue = this.formGroup.get(idCustomField)?.value;

    const updatedCustomFields = this.contact.customFields.map(field =>
      field._id === idCustomField ? { ...field, value: newValue } : field
    );

    this.contact = {
      ...this.contact,
      customFields: updatedCustomFields
    };

    this.updateContact();
  }

  removeCustomField(idCustomField: string): void {
    const updatedContact = {
      ...this.contact,
      customFields: this.contact?.customFields.filter(field => field._id !== idCustomField),
    };

    this.contact = updatedContact;
    if (this.formGroup.contains(idCustomField)) {
      this.formGroup.removeControl(idCustomField);
    }

    this.updateContact();
  }

  updateContact() {
    if (this.attendance.status === AttendanceStatusEnum.CLOSED) {
      this.alertService.info(this.t('attendance.customFieldAttendance.closedAttendanceWarning'));
      return;
    }

    // Send only the custom fields. `this.contact` comes from the attendance in the
    // NgRx store — a snapshot — and the update endpoint $sets every key it receives,
    // so posting the whole contact would revert tags, groups, leadSource and more.
    const payload = <Contact>{ _id: this.contact._id, customFields: this.contact.customFields };

    this.contactService.update(this.contact._id, payload).subscribe({
      next: (value) => {
        this.alertService.success(this.t('attendance.customFieldAttendance.contactUpdatedOk'));
        this.showSelectCustomField = true;
        this.store.dispatch(getAttendance({ idAttendance: this.attendance._id }));
      },
      complete: () => { }
    });
  }

  getFieldType(fieldId: string): string {
    const field = this.listFieldsCustom.find(item => item._id === fieldId);
    return field ? field.type : '';
  }
}
